/*
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';
import MapView from '../common/MapView';

const ComplaintDetail = () => {
  const { id } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [feedback, setFeedback] = useState({ rating: '', comment: '' });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const load = async () => {
    try {
      const data = await complaintService.getComplaint(id);
      setComplaint(data.complaint);
    } catch (err) {
      toast.error('Failed to load complaint');
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.rating) {
      toast.error('Please select rating');
      return;
    }
    setSubmittingFeedback(true);
    try {
      await complaintService.submitFeedback(id, {
        rating: Number(feedback.rating),
        comment: feedback.comment,
      });
      toast.success('Feedback submitted');
      setFeedback({ rating: '', comment: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (!complaint) return <LoadingSpinner />;

  const beforeImages = complaint.images?.before || [];
  const afterImages = complaint.images?.after || [];

  const lat = complaint.location?.coordinates?.[1];
  const lng = complaint.location?.coordinates?.[0];

  return (
    <div className="max-w-4xl mx-auto mt-6 bg-white shadow rounded-lg p-6">
      <div className="flex justify-between mb-3">
        <h2 className="text-xl font-semibold">{complaint.title}</h2>
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100">
          {complaint.status}
        </span>
      </div>
      <p className="text-sm text-gray-700 mb-2">{complaint.description}</p>
      <p className="text-xs text-gray-500 mb-1">
        Category: {complaint.category} | Priority: {complaint.priority}
      </p>
      <p className="text-xs text-gray-500 mb-2">
        Address: {complaint.location?.address}
      </p>
      <p className="text-xs text-gray-400 mb-4">
        Created: {new Date(complaint.createdAt).toLocaleString()}
      </p>

      <MapView lat={lat} lng={lng} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 mt-4">
        <div>
          <h3 className="font-semibold text-sm mb-2">Before</h3>
          <div className="flex flex-wrap gap-2">
            {beforeImages.length === 0 && (
              <p className="text-xs text-gray-400">No images</p>
            )}
            {beforeImages.map((img) => (
              <img
                key={img.publicId}
                src={img.url}
                alt="before"
                className="h-28 w-28 object-cover rounded-md"
              />
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-sm mb-2">After</h3>
          <div className="flex flex-wrap gap-2">
            {afterImages.length === 0 && (
              <p className="text-xs text-gray-400">Not updated yet</p>
            )}
            {afterImages.map((img) => (
              <img
                key={img.publicId}
                src={img.url}
                alt="after"
                className="h-28 w-28 object-cover rounded-md"
              />
            ))}
          </div>
        </div>
      </div>

      {complaint.status === 'resolved' && (
        <div className="mt-4 border-t pt-4">
          <h3 className="font-semibold text-sm mb-2">Your Feedback</h3>
          {complaint.feedback?.rating ? (
            <div className="text-sm text-gray-700">
              <p>Rating: {complaint.feedback.rating} / 5</p>
              {complaint.feedback.comment && (
                <p className="mt-1">
                  Comment: {complaint.feedback.comment}
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={submitFeedback} className="space-y-2 text-sm">
              <div>
                <label className="block mb-1">Rating</label>
                <select
                  value={feedback.rating}
                  onChange={(e) =>
                    setFeedback((prev) => ({
                      ...prev,
                      rating: e.target.value,
                    }))
                  }
                  className="border px-3 py-1 rounded-md"
                  required
                >
                  <option value="">Select rating</option>
                  {[1, 2, 3, 4, 5].map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1">Comment (optional)</label>
                <textarea
                  rows={3}
                  className="w-full border px-3 py-1 rounded-md"
                  value={feedback.comment}
                  onChange={(e) =>
                    setFeedback((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                />
              </div>
              <button
                type="submit"
                disabled={submittingFeedback}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-xs disabled:opacity-50"
              >
                {submittingFeedback ? 'Submitting...' : 'Submit feedback'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplaintDetail;
*/

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';
import MapView from '../common/MapView';

const STATUS_STEPS = ['pending', 'in_progress', 'resolved'];

const ComplaintDetail = () => {
  const { id } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [feedback, setFeedback] = useState({ rating: '', comment: '' });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const load = async () => {
    try {
      const data = await complaintService.getComplaint(id);
      setComplaint(data.complaint);
    } catch (err) {
      toast.error('Failed to load complaint');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.rating) {
      toast.error('Please select rating');
      return;
    }
    setSubmittingFeedback(true);
    try {
      await complaintService.submitFeedback(id, {
        rating: Number(feedback.rating),
        comment: feedback.comment,
      });
      toast.success('Feedback submitted');
      setFeedback({ rating: '', comment: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const currentStatusIndex = useMemo(() => {
    if (!complaint) return 0;
    return STATUS_STEPS.indexOf(complaint.status);
  }, [complaint]);

  if (!complaint) return <LoadingSpinner />;

  const beforeImages = complaint.images?.before || [];
  const afterImages = complaint.images?.after || [];

  const lat = complaint.location?.coordinates?.[1];
  const lng = complaint.location?.coordinates?.[0];

  return (
    <div className="max-w-5xl mx-auto mt-6 px-4">
      {/* Header card */}
      <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 mb-4 border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {complaint.title}
            </h2>
            <p className="text-xs text-gray-500 mb-1">
              Category: {complaint.category} • Priority: {complaint.priority}
            </p>
            <p className="text-xs text-gray-500 mb-1">
              Location: {complaint.location?.address}
            </p>
            <p className="text-[11px] text-gray-400">
              Created:{' '}
              {new Date(complaint.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 mr-1.5" />
              {complaint.status.replace('_', ' ')}
            </span>
            {complaint.feedback?.rating && (
              <div className="text-[11px] bg-green-50 text-green-700 px-2 py-1 rounded-full border border-green-100">
                ⭐ Citizen rated {complaint.feedback.rating}/5
              </div>
            )}
          </div>
        </div>

        {/* Status timeline */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
            <span>Pending</span>
            <span>In Progress</span>
            <span>Resolved</span>
          </div>
          <div className="relative mt-1">
            <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-gray-200" />
            <div
              className="absolute left-0 top-1/2 h-[2px] bg-indigo-500 transition-all"
              style={{
                width: `${(currentStatusIndex / (STATUS_STEPS.length - 1)) * 100}%`,
              }}
            />
            <div className="relative flex justify-between">
              {STATUS_STEPS.map((status, idx) => {
                const isActive = idx <= currentStatusIndex;
                return (
                  <div
                    key={status}
                    className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                      isActive
                        ? 'border-indigo-500 bg-white'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${
                        isActive ? 'bg-indigo-500' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Map + images */}
      <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 mb-4 border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          Location & Photos
        </h3>

        <MapView lat={lat} lng={lng} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">
              Before Cleaning
            </p>
            <div className="flex flex-wrap gap-2">
              {beforeImages.length === 0 && (
                <p className="text-xs text-gray-400">No images</p>
              )}
              {beforeImages.map((img) => (
                <img
                  key={img.publicId}
                  src={img.url}
                  alt="before"
                  className="h-24 w-24 object-cover rounded-md border"
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">
              After Cleaning
            </p>
            <div className="flex flex-wrap gap-2">
              {afterImages.length === 0 && (
                <p className="text-xs text-gray-400">
                  Not updated yet. The team is still working.
                </p>
              )}
              {afterImages.map((img) => (
                <img
                  key={img.publicId}
                  src={img.url}
                  alt="after"
                  className="h-24 w-24 object-cover rounded-md border"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Description & feedback */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            Description
          </h3>
          <p className="text-sm text-gray-700 whitespace-pre-line">
            {complaint.description}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Your Feedback
          </h3>

          {complaint.status !== 'resolved' && (
            <p className="text-xs text-gray-500">
              Feedback will be available once this complaint is marked as
              resolved.
            </p>
          )}

          {complaint.status === 'resolved' &&
            complaint.feedback?.rating && (
              <div className="text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Rating:</span>{' '}
                  {complaint.feedback.rating} / 5
                </p>
                {complaint.feedback.comment && (
                  <p>
                    <span className="font-medium">Comment:</span>{' '}
                    {complaint.feedback.comment}
                  </p>
                )}
              </div>
            )}

          {complaint.status === 'resolved' &&
            !complaint.feedback?.rating && (
              <form
                onSubmit={submitFeedback}
                className="space-y-3 text-sm mt-1"
              >
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Rating
                  </label>
                  <select
                    value={feedback.rating}
                    onChange={(e) =>
                      setFeedback((prev) => ({
                        ...prev,
                        rating: e.target.value,
                      }))
                    }
                    className="border px-3 py-2 rounded-md text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    required
                  >
                    <option value="">Select rating</option>
                    {[1, 2, 3, 4, 5].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Comment (optional)
                  </label>
                  <textarea
                    rows={3}
                    className="w-full border px-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    value={feedback.comment}
                    onChange={(e) =>
                      setFeedback((prev) => ({
                        ...prev,
                        comment: e.target.value,
                      }))
                    }
                    placeholder="Share your experience with the resolution."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submittingFeedback}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-full text-xs sm:text-sm disabled:opacity-60 hover:bg-indigo-700 shadow-sm"
                >
                  {submittingFeedback
                    ? 'Submitting...'
                    : 'Submit feedback'}
                </button>
              </form>
            )}
        </div>
      </div>
    </div>
  );
};

export default ComplaintDetail;
